import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";
import storage from '../nativeStorage';
import useIsOnline, { OfflineSetupBanner } from '../useIsOnline';

export default function Course() {
  const isOnline = useIsOnline();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [holes, setHoles] = useState([]);
  const [courseName, setCourseName] = useState("");
  const [selectedHole, setSelectedHole] = useState(null);
  const [editPar, setEditPar] = useState("");
  const [editSI, setEditSI] = useState("");
  const [editYardage, setEditYardage] = useState("");
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [reqCourseName, setReqCourseName] = useState("");
  const [reqLocation, setReqLocation] = useState("");
  const [reqNotes, setReqNotes] = useState("");
  const [reqSending, setReqSending] = useState(false);
  const [reqSent, setReqSent] = useState(false);
  const [showDbModal, setShowDbModal] = useState(false);
  const [dbStep, setDbStep] = useState("search"); // search | clubs | courses | tees | review
  const [dbSearchName, setDbSearchName] = useState("");
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState("");
  const [dbClubs, setDbClubs] = useState([]);
  const [dbSelectedClub, setDbSelectedClub] = useState(null);
  const [dbCourses, setDbCourses] = useState([]);
  const [dbSelectedCourseDetail, setDbSelectedCourseDetail] = useState(null);
  const [dbSelectedTee, setDbSelectedTee] = useState(null);
  const [creatingFromDb, setCreatingFromDb] = useState(false);

  useEffect(() => { loadCourses(); }, []);

  const loadCourses = async () => {
    try {
      const [coursesRes, eventRes] = await Promise.all([
        api.get("/courses"),
        api.get("/events/current")
      ]);
      const allCourses = coursesRes.data;
      setCourses(allCourses);
      if (allCourses.length > 0) {
        const eventCourseId = eventRes.data?.course_id;
        const toSelect = allCourses.find(c => c.id === eventCourseId) || allCourses[0];
        selectCourse(toSelect, !eventCourseId);
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const selectCourse = async (course, updateEvent = false) => {
    setSelectedCourse(course);
    setCourseName(course.name);
    setSelectedHole(null);
    // Only link course to active event when user explicitly selects it.
    // Course setup stays read-only offline (see OfflineSetupBanner below) --
    // skip outright rather than letting this PUT /events/:id fall into the
    // offline queue, since that same route+method is legitimately queueable
    // when it comes from Players.jsx's event-field edits.
    if (updateEvent && isOnline) {
      try {
        const eventRes = await api.get('/events/current');
        if (eventRes.data) {
          await api.put('/events/' + eventRes.data.id, { ...eventRes.data, course_id: course.id });
        }
      } catch(e) { console.error('Failed to link course:', e.message); }
    }
    try {
      const res = await api.get("/courses/" + course.id + "/holes");
      setHoles(res.data);
    } catch(e) { console.error(e); }
  };

  const selectHole = (hole) => {
    setSelectedHole(hole);
    setEditPar(String(hole.par));
    setEditSI(String(hole.stroke_index));
    setEditYardage(String(hole.yardage));
  };

  const updateHole = async () => {
    if (!selectedHole) return;
    if (!isOnline) return alert("You're offline — Course setup can't be edited without a connection.");
    try {
      await api.put("/courses/" + selectedCourse.id + "/holes/" + selectedHole.hole, {
        par: parseInt(editPar),
        stroke_index: parseInt(editSI),
        yardage: parseInt(editYardage)
      });
      setHoles(prev => prev.map(h => h.hole === selectedHole.hole
        ? { ...h, par: parseInt(editPar), stroke_index: parseInt(editSI), yardage: parseInt(editYardage) }
        : h
      ));
    } catch(e) { alert("Failed to update hole"); }
  };

  const saveCourse = async () => {
    if (!selectedCourse) return;
    if (!isOnline) return alert("You're offline — Course setup can't be edited without a connection.");
    try {
      await api.put("/courses/" + selectedCourse.id, { name: courseName });
      setCourses(prev => prev.map(c => c.id === selectedCourse.id ? { ...c, name: courseName } : c));
      setSelectedCourse(prev => ({ ...prev, name: courseName }));
      alert("Course saved!");
    } catch(e) { alert("Failed to save course"); }
  };

  const newCourse = async () => {
    if (!isOnline) return alert("You're offline — Course setup can't be edited without a connection.");
    const name = prompt("Enter new course name:");
    if (!name) return;
    try {
      const res = await api.post("/courses", { name });
      setCourses(prev => [...prev, res.data]);
      selectCourse(res.data);
    } catch(e) { alert("Failed to create course"); }
  };

  const importImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const name = prompt("Enter name for this course:");
    if (!name) return;
    setImporting(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const mediaType = file.type || "image/jpeg";
      const token = storage.getItem("token");
      const response = await fetch("/api/courseimport/import", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify({ image: base64, mediaType: mediaType })
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.error || "Import failed"); }
      const data = await response.json();
      const textBlock = (data.content || []).find(b => b.type === "text");
      const text = textBlock?.text || "";
      let holeData;
      try { holeData = JSON.parse(text.replace(/```json|```/g, "").trim()); }
      catch(pe) { throw new Error("Could not parse scorecard. Try a clearer image."); }
      const courseRes = await api.post("/courses", { name });
      const newCourseData = courseRes.data;
      for (const h of holeData) {
        await api.put("/courses/" + newCourseData.id + "/holes/" + h.hole, { par: h.par||4, stroke_index: h.stroke_index||h.hole, yardage: h.yardage||0 });
      }
      const res = await api.get("/courses");
      setCourses(res.data);
      const created = res.data.find(c => c.id === newCourseData.id);
      if (created) selectCourse(created);
      alert("Course imported successfully!");
    } catch(e) {
      console.error(e);
      alert("Import failed: " + e.message);
    } finally {
      setImporting(false);
    }
  };

  const closeDbModal = () => {
    setShowDbModal(false);
    setDbStep("search"); setDbSearchName(""); setDbError(""); setDbClubs([]);
    setDbSelectedClub(null); setDbCourses([]); setDbSelectedCourseDetail(null); setDbSelectedTee(null);
  };

  const runDbSearch = async () => {
    if (!dbSearchName.trim()) { setDbError("Enter a course name"); return; }
    setDbLoading(true); setDbError("");
    try {
      const token = storage.getItem("token");
      const res = await fetch("/api/coursedb/clubs?search=" + encodeURIComponent(dbSearchName.trim()), {
        headers: { "Authorization": "Bearer " + token }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Lookup failed");
      if (!data.length) { setDbError("No clubs found matching that name in the UK golf database. Try Search Web or Import Image instead."); setDbLoading(false); return; }
      setDbClubs(data);
      setDbStep("clubs");
    } catch (e) {
      setDbError(e.message || "Lookup failed");
    } finally {
      setDbLoading(false);
    }
  };

  const pickDbClub = async (club) => {
    setDbSelectedClub(club);
    setDbLoading(true); setDbError("");
    try {
      const token = storage.getItem("token");
      const res = await fetch("/api/coursedb/clubs/" + club.id + "/courses", {
        headers: { "Authorization": "Bearer " + token }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Lookup failed");
      if (!data.length) { setDbError("No course data available for this club."); setDbLoading(false); return; }
      setDbCourses(data);
      if (data.length === 1) {
        await pickDbCourse(data[0]);
      } else {
        setDbStep("courses");
        setDbLoading(false);
      }
    } catch (e) {
      setDbError(e.message || "Lookup failed");
      setDbLoading(false);
    }
  };

  const pickDbCourse = async (course) => {
    setDbLoading(true); setDbError("");
    try {
      const token = storage.getItem("token");
      const res = await fetch("/api/coursedb/courses/" + course.id, {
        headers: { "Authorization": "Bearer " + token }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Lookup failed");
      if (!data.tee_sets || !data.tee_sets.length) { setDbError("No tee data available for this course."); setDbLoading(false); return; }
      setDbSelectedCourseDetail(data);
      setDbStep("tees");
    } catch (e) {
      setDbError(e.message || "Lookup failed");
    } finally {
      setDbLoading(false);
    }
  };

  const pickDbTee = (tee) => {
    setDbSelectedTee(tee);
    setDbStep("review");
  };

  const createCourseFromDb = async () => {
    if (!dbSelectedTee || !dbSelectedClub) return;
    setCreatingFromDb(true);
    try {
      const name = dbSelectedClub.name + (dbSelectedCourseDetail.name && dbSelectedCourseDetail.name !== dbSelectedClub.name ? " - " + dbSelectedCourseDetail.name : "");
      // Store every tee set this course has (colour/gender/yardage/par/
      // rating), not just the one chosen for scoring — this is what lets
      // Players.jsx later only offer Tee Color options that genuinely exist
      // for the course actually linked to the event. The full per-hole
      // breakdown is dropped here since course_holes already holds the one
      // chosen tee's hole-by-hole data; re-storing every tee's 18 holes a
      // second time here would just be dead weight.
      const teeSetsMeta = (dbSelectedCourseDetail.tee_sets || []).map(t => ({
        name: t.name, colour: t.colour, gender: t.gender,
        total_yardage: t.total_yardage, total_metres: t.total_metres,
        par: t.par, course_rating: t.course_rating, slope_rating: t.slope_rating,
      }));
      const courseRes = await api.post("/courses", {
        name,
        latitude: dbSelectedClub.latitude,
        longitude: dbSelectedClub.longitude,
        tee_sets: teeSetsMeta,
      });
      const newCourseData = courseRes.data;
      for (const h of dbSelectedTee.holes) {
        await api.put("/courses/" + newCourseData.id + "/holes/" + h.hole_number, {
          par: h.par || 4, stroke_index: h.stroke_index || h.hole_number, yardage: h.yardage || 0
        });
      }
      const res = await api.get("/courses");
      setCourses(res.data);
      const created = res.data.find(c => c.id === newCourseData.id);
      if (created) selectCourse(created);
      closeDbModal();
      alert("Course created from the UK Golf Course Database (" + dbSelectedTee.name + " tees) — real published data, no need to double-check.");
    } catch (e) {
      alert("Failed to create course: " + e.message);
    } finally {
      setCreatingFromDb(false);
    }
  };

  const submitCourseRequest = async () => {
    if (!reqCourseName || !reqLocation) { alert("Please enter course name and location"); return; }
    setReqSending(true);
    try {
      const token = storage.getItem("token");
      const res = await fetch("/api/courserequest/request", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify({ courseName: reqCourseName, location: reqLocation, notes: reqNotes })
      });
      if (!res.ok) throw new Error("Failed to send");
      setReqSent(true);
      setTimeout(() => {
        setShowRequestModal(false);
        setReqSent(false);
        setReqCourseName(""); setReqLocation(""); setReqNotes("");
      }, 2000);
    } catch(e) {
      alert("Failed to send request. Please try again.");
    } finally {
      setReqSending(false);
    }
  };

  const deleteCourse = async () => {
    if (!selectedCourse) return;
    if (!isOnline) return alert("You're offline — Course setup can't be edited without a connection.");
    if (!window.confirm("Delete " + selectedCourse.name + "?")) return;
    try {
      await api.delete("/courses/" + selectedCourse.id);
      const remaining = courses.filter(c => c.id !== selectedCourse.id);
      setCourses(remaining);
      if (remaining.length > 0) selectCourse(remaining[0]);
      else { setSelectedCourse(null); setHoles([]); setCourseName(""); }
    } catch(e) { alert("Failed to delete course"); }
  };

  const totalPar = holes.reduce((sum, h) => sum + (h.par || 0), 0);
  const totalYardage = holes.reduce((sum, h) => sum + (h.yardage || 0), 0);

  return (
    <Layout>
      <div className="flex flex-col h-full">
        {!isOnline && <OfflineSetupBanner screen="Course Setup" />}
        <div className="bg-green-50 border-b border-gray-300 px-4 py-2 flex items-center gap-3 flex-wrap">
          <span className="font-semibold text-green-800 text-sm">Course Setup</span>
          <span className="text-xs text-gray-500 italic">Persistent — not affected by Reset Game.</span>
          <div className="flex items-center gap-2 ml-4">
            <span className="text-xs font-medium text-gray-600">Select Cou...</span>
            <select value={selectedCourse?.id || ""} onChange={e => { const c = courses.find(c => c.id === parseInt(e.target.value)); if (c) selectCourse(c, true); }}
              className="border border-gray-300 rounded px-2 py-1 text-xs w-36">
              {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-600">Course Na...</span>
            <input type="text" value={courseName} onChange={e => setCourseName(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-xs w-32" />
          </div>
          <span className="text-xs font-medium text-gray-700">Total Par: <strong className="text-green-800">{totalPar}</strong></span>
          <span className="text-xs font-medium text-gray-700">Total Yardage: <strong className="text-green-800">{totalYardage}</strong></span>
          <div className="ml-auto flex gap-2">
            <button onClick={newCourse} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">New Course</button>
            <label className={"bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded cursor-pointer " + (importing ? "opacity-50" : "")}>
              {importing ? "Importing..." : "Import Image"}
              <input type="file" accept="image/*" onChange={importImage} className="hidden" disabled={importing} />
            </label>
            <button onClick={() => setShowDbModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded">UK Golf Database</button>
            <button onClick={saveCourse} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Save Course</button>
            <button onClick={deleteCourse} className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Delete Course</button>
          </div>
        </div>

        {selectedCourse?.photo_url && (
          <div style={{ padding: "10px 16px", background: "white", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: "12px" }}>
            <img src={selectedCourse.photo_url} alt={selectedCourse.name + " aerial view"}
              style={{ width: "160px", height: "107px", objectFit: "cover", borderRadius: "8px", border: "1px solid #d1d5db" }} />
            <div style={{ fontSize: "12px", color: "#6b7280" }}>Aerial view of {selectedCourse.name}</div>
          </div>
        )}

        <div onClick={() => setShowRequestModal(true)}
          style={{ background: "linear-gradient(90deg, #7c3aed, #6d28d9)", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", cursor: "pointer", borderBottom: "1px solid #5b21b6" }}>
          <span style={{ fontSize: "20px" }}>🌍</span>
          <span style={{ color: "white", fontWeight: "800", fontSize: "14px" }}>Can't find your course? Request it to be added — anywhere in the world!</span>
        </div>

        {showRequestModal && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
            <div style={{ background: "white", borderRadius: "12px", padding: "24px", width: "100%", maxWidth: "420px", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
              {reqSent ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ fontSize: "40px", marginBottom: "10px" }}>✅</div>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#14532d" }}>Request Sent!</div>
                  <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "6px" }}>We'll add this course as soon as possible.</div>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                    <h2 style={{ fontSize: "16px", fontWeight: "800", color: "#14532d" }}>🌍 Request a Course</h2>
                    <button onClick={() => setShowRequestModal(false)} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#9ca3af" }}>×</button>
                  </div>
                  <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "16px" }}>Tell us the course name and location — anywhere in the world — and we'll add the scorecard data for you.</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    <div>
                      <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "4px" }}>Course Name *</label>
                      <input type="text" value={reqCourseName} onChange={e => setReqCourseName(e.target.value)} placeholder="e.g. St Andrews Old Course"
                        style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "6px", padding: "8px 10px", fontSize: "13px" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "4px" }}>Location *</label>
                      <input type="text" value={reqLocation} onChange={e => setReqLocation(e.target.value)} placeholder="e.g. St Andrews, Fife, Scotland"
                        style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "6px", padding: "8px 10px", fontSize: "13px" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "4px" }}>Notes (optional)</label>
                      <textarea value={reqNotes} onChange={e => setReqNotes(e.target.value)} placeholder="Any additional details"
                        style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "6px", padding: "8px 10px", fontSize: "13px", minHeight: "60px" }} />
                    </div>
                    <button onClick={submitCourseRequest} disabled={reqSending}
                      style={{ background: "#7c3aed", color: "white", border: "none", borderRadius: "8px", padding: "12px", fontSize: "14px", fontWeight: "700", cursor: "pointer", opacity: reqSending ? 0.6 : 1 }}>
                      {reqSending ? "Sending..." : "Send Request"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {showDbModal && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
            <div style={{ background: "white", borderRadius: "12px", padding: "24px", width: "100%", maxWidth: dbStep === "review" ? "600px" : "460px", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                <h2 style={{ fontSize: "16px", fontWeight: "800", color: "#4338ca" }}>🇬🇧 UK Golf Course Database</h2>
                <button onClick={closeDbModal} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#9ca3af" }}>×</button>
              </div>

              {dbStep === "search" && (
                <>
                  <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "16px" }}>
                    Looks up real, published data for 2,668 UK clubs — instant and always accurate, since it's a structured database, not AI guesswork.
                  </p>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "4px" }}>Course Name *</label>
                  <input type="text" value={dbSearchName} onChange={e => setDbSearchName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && runDbSearch()}
                    placeholder="e.g. Hagley" disabled={dbLoading} autoFocus
                    style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "6px", padding: "8px 10px", fontSize: "13px", marginBottom: "12px" }} />
                  {dbError && <div style={{ fontSize: "12px", color: "#b91c1c", background: "#fef2f2", padding: "8px 10px", borderRadius: "6px", marginBottom: "12px" }}>{dbError}</div>}
                  <button onClick={runDbSearch} disabled={dbLoading}
                    style={{ width: "100%", background: "#4338ca", color: "white", border: "none", borderRadius: "8px", padding: "12px", fontSize: "14px", fontWeight: "700", cursor: "pointer", opacity: dbLoading ? 0.6 : 1 }}>
                    {dbLoading ? "Searching..." : "Search"}
                  </button>
                </>
              )}

              {dbStep === "clubs" && (
                <>
                  <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "10px" }}>
                    {dbClubs.length > 1 ? "Multiple clubs matched — pick the right one:" : "Confirm this is the right club:"}
                  </p>
                  {dbError && <div style={{ fontSize: "12px", color: "#b91c1c", background: "#fef2f2", padding: "8px 10px", borderRadius: "6px", marginBottom: "12px" }}>{dbError}</div>}
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" }}>
                    {dbClubs.map(c => (
                      <button key={c.id} onClick={() => pickDbClub(c)} disabled={dbLoading}
                        style={{ textAlign: "left", border: "1px solid #d1d5db", borderRadius: "8px", padding: "10px 12px", background: "white", cursor: "pointer", opacity: dbLoading ? 0.6 : 1 }}>
                        <div style={{ fontWeight: 700, fontSize: "13px", color: "#1f2937" }}>{c.name}</div>
                        <div style={{ fontSize: "11px", color: "#6b7280" }}>{c.city}{c.county ? ", " + c.county : ""}{c.postcode ? " — " + c.postcode : ""}</div>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setDbStep("search")} disabled={dbLoading}
                    style={{ background: "#e5e7eb", color: "#374151", border: "none", borderRadius: "8px", padding: "8px 12px", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}>
                    ← Back
                  </button>
                </>
              )}

              {dbStep === "courses" && (
                <>
                  <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "10px" }}>{dbSelectedClub?.name} has multiple courses — pick one:</p>
                  {dbError && <div style={{ fontSize: "12px", color: "#b91c1c", background: "#fef2f2", padding: "8px 10px", borderRadius: "6px", marginBottom: "12px" }}>{dbError}</div>}
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" }}>
                    {dbCourses.map(c => (
                      <button key={c.id} onClick={() => pickDbCourse(c)} disabled={dbLoading}
                        style={{ textAlign: "left", border: "1px solid #d1d5db", borderRadius: "8px", padding: "10px 12px", background: "white", cursor: "pointer", opacity: dbLoading ? 0.6 : 1 }}>
                        <div style={{ fontWeight: 700, fontSize: "13px", color: "#1f2937" }}>{c.name}</div>
                        <div style={{ fontSize: "11px", color: "#6b7280" }}>{c.holes} holes, par {c.par}</div>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setDbStep("clubs")} disabled={dbLoading}
                    style={{ background: "#e5e7eb", color: "#374151", border: "none", borderRadius: "8px", padding: "8px 12px", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}>
                    ← Back
                  </button>
                </>
              )}

              {dbStep === "tees" && dbSelectedCourseDetail && (
                <>
                  <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "10px" }}>Which tees do you play from?</p>
                  {dbError && <div style={{ fontSize: "12px", color: "#b91c1c", background: "#fef2f2", padding: "8px 10px", borderRadius: "6px", marginBottom: "12px" }}>{dbError}</div>}
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" }}>
                    {dbSelectedCourseDetail.tee_sets.map(t => (
                      <button key={t.id} onClick={() => pickDbTee(t)}
                        style={{ textAlign: "left", border: "1px solid #d1d5db", borderRadius: "8px", padding: "10px 12px", background: "white", cursor: "pointer", display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ width: "14px", height: "14px", borderRadius: "50%", background: t.colour ? t.colour.toLowerCase() : "#ccc", border: "1px solid #9ca3af", flexShrink: 0 }}></span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "13px", color: "#1f2937" }}>{t.name} {t.gender ? "(" + t.gender + ")" : ""}</div>
                          <div style={{ fontSize: "11px", color: "#6b7280" }}>Par {t.par} · {t.total_yardage} yds · Rating {t.course_rating} · Slope {t.slope_rating}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setDbStep(dbCourses.length > 1 ? "courses" : "clubs")}
                    style={{ background: "#e5e7eb", color: "#374151", border: "none", borderRadius: "8px", padding: "8px 12px", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}>
                    ← Back
                  </button>
                </>
              )}

              {dbStep === "review" && dbSelectedTee && (
                <>
                  <div style={{ fontSize: "12px", color: "#166534", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "8px 10px", borderRadius: "6px", marginBottom: "12px", fontWeight: 600 }}>
                    ✓ Real published data for {dbSelectedClub?.name} — {dbSelectedTee.name} tees. No need to double-check this.
                  </div>
                  <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse", marginBottom: "16px" }}>
                    <thead>
                      <tr style={{ background: "#4338ca", color: "white" }}>
                        <th style={{ padding: "4px 6px", textAlign: "left" }}>Hole</th>
                        <th style={{ padding: "4px 6px", textAlign: "left" }}>Par</th>
                        <th style={{ padding: "4px 6px", textAlign: "left" }}>SI</th>
                        <th style={{ padding: "4px 6px", textAlign: "left" }}>Yardage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dbSelectedTee.holes.map((h, i) => (
                        <tr key={h.hole_number} style={{ background: i % 2 === 0 ? "#eef2ff" : "white" }}>
                          <td style={{ padding: "4px 6px" }}>{h.hole_number}</td>
                          <td style={{ padding: "4px 6px" }}>{h.par}</td>
                          <td style={{ padding: "4px 6px" }}>{h.stroke_index ?? "—"}</td>
                          <td style={{ padding: "4px 6px" }}>{h.yardage || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={() => setDbStep("tees")} disabled={creatingFromDb}
                      style={{ flex: 1, background: "#e5e7eb", color: "#374151", border: "none", borderRadius: "8px", padding: "10px", fontSize: "13px", fontWeight: "700", cursor: "pointer" }}>
                      ← Change Tees
                    </button>
                    <button onClick={createCourseFromDb} disabled={creatingFromDb}
                      style={{ flex: 1, background: "#4338ca", color: "white", border: "none", borderRadius: "8px", padding: "10px", fontSize: "13px", fontWeight: "700", cursor: "pointer", opacity: creatingFromDb ? 0.6 : 1 }}>
                      {creatingFromDb ? "Creating..." : "Create Course"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div className="bg-yellow-50 border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="text-xs font-medium text-gray-700">Edit selected hole:</span>
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-600">Par:</span>
            <input type="number" value={editPar} onChange={e => setEditPar(e.target.value)}
              placeholder="3-5" min="3" max="5"
              className="border border-gray-300 rounded px-2 py-1 text-xs w-16" />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-600">Stroke Index:</span>
            <input type="number" value={editSI} onChange={e => setEditSI(e.target.value)}
              placeholder="1-18" min="1" max="18"
              className="border border-gray-300 rounded px-2 py-1 text-xs w-16" />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-600">Yardage:</span>
            <input type="number" value={editYardage} onChange={e => setEditYardage(e.target.value)}
              placeholder="0"
              className="border border-gray-300 rounded px-2 py-1 text-xs w-20" />
          </div>
          <button onClick={updateHole} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Update Hole</button>
          <span className="text-xs text-gray-500">{selectedHole ? "Hole " + selectedHole.hole + " selected" : "No hole selected"}</span>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm border-collapse border border-gray-300">
            <colgroup>
              <col style={{width:"15%"}} />
              <col style={{width:"20%"}} />
              <col style={{width:"30%"}} />
              <col style={{width:"35%"}} />
            </colgroup>
            <thead className="sticky top-0">
              <tr className="bg-green-800 text-white">
                <th className="px-4 py-2 font-semibold text-left border-r border-green-700">Hole</th>
                <th className="px-4 py-2 font-semibold text-left border-r border-green-700">Par</th>
                <th className="px-4 py-2 font-semibold text-left border-r border-green-700">Stroke Index</th>
                <th className="px-4 py-2 font-semibold text-left">Yardage</th>
              </tr>
            </thead>
            <tbody>
              {holes.map((hole, i) => (
                <tr key={hole.hole}
                  onClick={() => selectHole(hole)}
                  className={"cursor-pointer border-b border-gray-200 " + (
                    selectedHole?.hole === hole.hole ? "bg-blue-100" :
                    i % 2 === 0 ? "bg-white" : "bg-green-50"
                  )}>
                  <td className="px-4 py-2 border-r border-gray-200 font-medium">{hole.hole}</td>
                  <td className="px-4 py-2 border-r border-gray-200">{hole.par}</td>
                  <td className="px-4 py-2 border-r border-gray-200">{hole.stroke_index}</td>
                  <td className="px-4 py-2">{hole.yardage}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && holes.length === 0 && (
            <div className="text-center py-8 text-gray-500">No holes found for this course.</div>
          )}
        </div>
      </div>
    </Layout>
  );
}
